<template>
  <div class="w-100 h-100" id="listings-map">
    <div class="listings-map w-100 h-100 relative" v-if="properties.length">
      <div class="w-100 h-100 listings-map-property-slider" v-if="focusedProperties">
        <div class="focused-properties-backdrop fade-in" />
        <swiper
          class="relative listings-map-swiper"
          :options="swiperOptions"
          @click.self="focusedProperties = null"
        >
          <swiper-slide
            v-for="(property, index) in focusedProperties"
            :key="`property-slider-top-${index}`"
            @click.self="focusedProperties = null"
          >
            <div class="swiper-card-container" @click.self="focusedProperties = null">
              <div class="w-100 relative" @click="onListingCardClick(property.id)">
                <div class="map-card-close fade-in" @click="focusedProperties = null">
                  <img src="@/assets/icons/close.svg" />
                </div>
                <router-link
                  :to="{
                    name: 'Property',
                    params: {
                      id: property.id,
                      slug: property.slug,
                      market: property.market.slug,
                    },
                    query: $route.query,
                  }"
                >
                  <property-card
                    :property="property"
                    :hide-roommates="!interestFilterActive"
                    clickable
                    hide-actions
                    topCardPosition="left"
                  />
                </router-link>
              </div>
            </div>
          </swiper-slide>

          <div
            v-if="focusedProperties.length > 1 && !isMobile"
            class="map-swiper map-swiper-prev swiper-button swiper-button-prev"
            slot="button-prev"
          >
            <img src="@/assets/icons/arrow-left.svg" />
          </div>

          <div
            v-if="focusedProperties.length > 1 && !isMobile"
            class="map-swiper map-swiper-next swiper-button swiper-button-next"
            slot="button-next"
          >
            <img src="@/assets/icons/arrow-right.svg" />
          </div>
        </swiper>
      </div>

      <div v-if="showReCenterButton" @click="resetMapClickHandler" class="reset-map fade-in">
        Reset
      </div>
      <div class="w-100 h-100" id="mapbox-listings-map" />
    </div>
  </div>
</template>

<script>
import { get } from 'lodash-es'
/**
 * MAPBOX DOCUMENTATION LINKS
 * cluster filtering => https://docs.mapbox.com/mapbox-gl-js/style-spec/#other-filter
 * using clustered => https://blog.mapbox.com/clustering-properties-with-mapbox-and-html-markers-bb353c8662ba
 * MAPBOX API => https://docs.mapbox.com/mapbox-gl-js/api/
 * Geocoder => https://github.com/mapbox/mapbox-gl-geocoder/blob/master/API.md#parameters
 */

const MAX_ZOOM_LEVEL = 15
const MIN_ZOOM_LEVEL = 8
const GEO_CODE_ZOOM_LEVEL = 12
const MAPBOX_API_KEY = process.env.VUE_APP_MAPBOX_API_KEY
const MAPBOX_PADDING = { top: 50, bottom: 130, left: 50, right: 50 }
const MAPBOX_STYLE_URL = process.env.VUE_APP_MAPBOX_STYLE_URL

import { distance } from '@/utils/mapping.js'

export default {
  props: {
    market: {
      type: Object,
      required: true,
    },
    isMobile: {
      type: Boolean,
      default: false,
    },
    properties: {
      type: Array,
      default: () => [],
    },
    propertyHovered: {
      type: Object,
    },
    interestFilterActive: {
      type: Boolean,
      required: true,
    },
    setVisibleMapProperties: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      initialLoadComplete: false,
      showReCenterButton: false,
      focusedPropertyCluster: null,
      propertyMapMarkers: {},
      mapboxMap: null,
      focusedProperties: null,
      unClusteredProperties: [],
      locationSearchRef: null,
      swiperOptions: {
        simulateTouch: false,
        slidesPerView: 'auto',
        grabCursor: false,
        centeredSlides: true,
        spaceBetween: 20,
        navigation: {
          nextEl: '.map-swiper-next',
          prevEl: '.map-swiper-prev',
        },
      },
    }
  },
  mounted() {
    this.initMapBox()
  },
  computed: {
    marketBoundingBox() {
      const { marketArea } = this.market
      if (!marketArea) return []

      // The order of this array matters: [W, S, E, N] 🧭
      return [
        parseFloat(marketArea.boundsWest),
        parseFloat(marketArea.boundsSouth),
        parseFloat(marketArea.boundsEast),
        parseFloat(marketArea.boundsNorth),
      ]
    },
  },
  watch: {
    propertyHovered() {
      if (this.initialLoadComplete) this.mapboxOnIdleHandler()
    },
    properties(newProperties) {
      this.propertiesChangeHandler(newProperties)
    },
    unClusteredProperties(newUnClusteredProperties) {
      newUnClusteredProperties.forEach((newPropertySlug) => {
        const propertyToDisplay = this.properties.find(({ slug }) => slug === newPropertySlug)
        propertyToDisplay && this.createPropertyMapMarker(propertyToDisplay, null, this.mapboxMap)
      })
      this.deleteOldPropertyMarkers(newUnClusteredProperties)
    },
  },
  methods: {
    initMapBox() {
      window.mapboxgl.accessToken = MAPBOX_API_KEY

      const map = new window.mapboxgl.Map({
        container: 'mapbox-listings-map',
        style: MAPBOX_STYLE_URL,
        center: [this.properties[0].address.longitude, this.properties[0].address.latitude],
        maxZoom: MAX_ZOOM_LEVEL,
        minZoom: MIN_ZOOM_LEVEL,
      })
      map.on('load', this.mapboxOnLoadHandler)
      // these listeners conditionally render property markers that are now included in clusters
      map.on('move', this.mapMovementHandler)
      map.on('zoom', this.mapMovementHandler)
      // when the map is idle, render property markers and conditionally highlight clusters or markers
      map.on('idle', this.mapboxOnIdleHandler)
      // cursor styling for when the cluster is focused
      map.on('mouseenter', 'clusters', () => {
        map.getCanvas().style.cursor = 'pointer'
      })
      map.on('mouseleave', 'clusters', () => {
        map.getCanvas().style.cursor = ''
      })

      this.mapboxMap = map
    },
    /**
     * Loop through the existing property markers to determine if any are no longer in frame
     * If they aren't remove them from the map and delete them from the locally tracked property markers
     */
    deleteOldPropertyMarkers(newProperties) {
      Object.keys(this.propertyMapMarkers).forEach((propertySlug) => {
        const propertyHasBeenFilteredOut = !newProperties.find(
          (newPropertySlug) => newPropertySlug === propertySlug
        )
        // if the marker has been filtered out remove the marker
        if (propertyHasBeenFilteredOut) {
          // remove the marker
          this.propertyMapMarkers[propertySlug].remove()
          // remove the property marker from the list of existing markers
          delete this.propertyMapMarkers[propertySlug]
        }
      })
    },
    // Gets all invisible unclustered map markers and renders a custom property marker ontop of that marker
    renderPropertyMarkers() {
      if (!this.initialLoadComplete) return

      // grab the propertyListings data source
      const clusterSource = this.mapboxMap.getSource('propertyListings')
      // only attempt to highlight clusters if they have been rendered on the map
      if (!clusterSource) return

      this.focusedPropertyCluster && this.focusedPropertyCluster.remove()

      let unClusteredMapboxMarkers = this.mapboxMap.queryRenderedFeatures({
        layers: ['unclustered-properties'],
      })

      let unClusteredProperties = []

      unClusteredMapboxMarkers.forEach((clusterProperty) => {
        const clusterPropertySlug = get(clusterProperty, 'properties.propertySlug')
        unClusteredProperties.push(clusterPropertySlug)
      })

      this.unClusteredProperties = unClusteredProperties
    },
    /**
     * Initial mapbox on load handler, sets up the bounds, renders the mapbox clusters and ensures
     * the map is properly sized with the clusters and markers in mind
     */
    mapboxOnLoadHandler() {
      const map = this.mapboxMap

      // base map config
      const locationSearch = new window.MapboxGeocoder({
        accessToken: MAPBOX_API_KEY,
        mapboxgl: window.mapboxgl,
        zoom: GEO_CODE_ZOOM_LEVEL,
        countries: 'us',
        placeholder: 'Search',
        bbox: this.marketBoundingBox,
      })
      // on search result click listener
      locationSearch.on('result', this.geocodeOnSearchResultClickedHandler)
      locationSearch.on('results', this.geocodeOnSearchResultsHandler)
      map.addControl(locationSearch)
      map.addControl(new window.mapboxgl.NavigationControl())

      const bounds = new window.mapboxgl.LngLatBounds()
      this.setMapboxClusterData(map)

      this.configureMapBounds(this.properties, bounds)

      map.fitBounds(bounds, {
        padding: MAPBOX_PADDING,
      })
      this.configurePropertyClusters(map)

      this.locationSearchRef = locationSearch
    },
    geocodeOnSearchResultsHandler() {
      this.$segment.track('CTA Clicked', {
        CTA: 'Enter map search',
        text: 'Enter map search',
        category: 'Listings page',
        market: this.$route.params.market,
      })
    },
    geocodeOnSearchResultClickedHandler(e) {
      try {
        // de-focus input element
        const inputElement = document.querySelector('input.mapboxgl-ctrl-geocoder--input')
        if (inputElement) inputElement.blur()
        this.$segment.track('CTA Clicked', {
          CTA: 'Map search',
          text: 'Map search',
          category: 'Listings page',
          market: this.$route.params.market,
        })
        const {
          result: {
            geometry: { coordinates },
          },
        } = e
        // sort the properties by distance to selected search result
        const topFourNearestProperties = this.properties
          .sort((p1, p2) => {
            return (
              distance(p1.address.latitude, p1.address.longitude, coordinates[1], coordinates[0]) -
              distance(p2.address.latitude, p2.address.longitude, coordinates[1], coordinates[0])
            )
          })
          .slice(0, 4)
        // create new bounds
        const bounds = new window.mapboxgl.LngLatBounds()
        // extend bounds for the nearest properties and the selected address
        bounds.extend(coordinates)
        this.configureMapBounds(topFourNearestProperties, bounds)
        // fit the bounds
        this.mapboxMap.fitBounds(bounds, { padding: MAPBOX_PADDING })
      } catch (e) {
        this.$sentry.captureException(e)
      }
    },
    configureMapBounds(properties, bounds) {
      properties.forEach((property) => {
        const {
          address: { latitude: propertyLatitude, longitude: propertyLongitude },
        } = property
        // extend the bounds of the map
        bounds.extend([propertyLongitude, propertyLatitude])
      })
    },
    /**
     * this creates a property marker ( ONLY IF IT DOESN'T ALREADY EXIST )
     * this marker handles on hover, and on click
     */
    createPropertyMapMarker(property, bounds, map) {
      const {
        address: { latitude: propertyLatitude, longitude: propertyLongitude },
        slug,
      } = property
      // extend the bounds of the map
      bounds && bounds.extend([propertyLongitude, propertyLatitude])
      // if the marker already exist, simply extend the bounds and return. No need to re-draw
      if (this.propertyMapMarkers[slug]) return

      // create the element of the marker ( as per mapbox docs )
      const propertyMarkerElement = document.createElement('div')
      propertyMarkerElement.innerHTML = `${property._isMatch ? '⚡️' : ''}${property.priceDisplay}`
      propertyMarkerElement.className = 'property-marker'
      propertyMarkerElement.id = `property-marker-${slug}`
      // Property marker event handlers
      propertyMarkerElement.addEventListener('click', () => {
        this.mapMarketClickHandler({
          position: [propertyLongitude, propertyLatitude],
          property,
        })
      })
      // Create create map market for property
      // 1. add market to map
      const propertyMarker = new window.mapboxgl.Marker(propertyMarkerElement)
        .setLngLat([propertyLongitude, propertyLatitude])
        .addTo(map)
      // add marker to recorded list of property markers
      this.propertyMapMarkers[slug] = propertyMarker
    },
    /**
     * convert properties to a geojson source which is then used
     * for the mapbox clusters and markers
     * SEE TOP LEVEL COMMENT FOR CLUSTERING DOCS!!!
     */
    setMapboxClusterData(map) {
      const clusterData = this.convertPropertiesToGeoJSON(this.properties)
      const existingClusterSource = map.getSource('propertyListings')
      if (existingClusterSource) {
        existingClusterSource.setData({
          type: 'FeatureCollection',
          features: clusterData,
        })
      } else {
        map.addSource('propertyListings', {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: clusterData,
          },
          cluster: true,
          clusterMaxZoom: MAX_ZOOM_LEVEL,
          clusterRadius: 40,
        })
      }
    },
    /**
     * this is where the magic happens
     * we render the clusters spcifying the cluster radius, render the cluster count
     * ( how many properties are inside of the cluster)
     * and add click handlers to the cluster
     */
    configurePropertyClusters(map) {
      /**
       * create a map layer for clustered data,
       * this uses the data source we created earlier as the `source` field, telling mapbox which data to cluster
       */
      map.addLayer({
        id: 'clusters',
        type: 'circle',
        source: 'propertyListings',
        filter: ['has', 'point_count'],
        paint: {
          'circle-color': '#241f21',
          'circle-radius': 20,
        },
      })
      /**
       * this renders a count of the number of properties inside each cluster
       */
      map.addLayer({
        id: 'cluster-count',
        type: 'symbol',
        source: 'propertyListings',
        filter: ['has', 'point_count'],
        layout: {
          'text-field': '{point_count_abbreviated}',
          'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
          'text-size': 15,
        },
        paint: {
          'text-color': '#ffffff',
        },
      })
      /**
       * this is sneaky but epic
       * using the mapbox filters which can be found here (https://docs.mapbox.com/mapbox-gl-js/style-spec/#other-filter)
       * for each property NOT inside of a cluster, we render an invisible cluster point on the map
       * we then ( in our renderPropertyMarkers function ) loop over these layer, and render a custom marker
       */
      map.addLayer({
        id: 'unclustered-properties',
        type: 'circle',
        source: 'propertyListings',
        filter: ['!has', 'point_count'],
        paint: {
          'circle-radius': 0,
        },
      })
      /**
       * when a cluster is clicked, zoom into the map 1 step
       * IF a cluster is clucked and we cannot zoom futher, open that clusters properties for viewing
       */
      map.on('click', 'clusters', (e) => {
        // grab the cluster at the point the user has clicked
        const features = this.mapboxMap.queryRenderedFeatures(e.point, {
          layers: ['clusters'],
        })
        // track the point count to amplitude on click
        const point_count = features[0].properties.point_count
        // get the clusterId so we can zoom and pan into it
        const clusterId = features[0].properties.cluster_id

        this.$segment.track('Map Cluster Click', {
          category: 'Listings',
          label: 'Property Cluster',
          value: point_count,
        })

        // get the cluster source so we can query the clicked cluster
        const clusterSource = this.mapboxMap.getSource('propertyListings')
        // get the maps current zoom for logic down beelloowww
        const currentZoomLevel = this.mapboxMap.getZoom()
        // zoom to the clusters zenter and bump the zoom level of the map by 1
        clusterSource.getClusterExpansionZoom(clusterId, (err) => {
          if (err) return

          const NEW_MAP_ZOOM_LEVEL = Math.ceil(currentZoomLevel + 1)
          this.mapboxMap.easeTo({
            center: features[0].geometry.coordinates,
            zoom: NEW_MAP_ZOOM_LEVEL,
          })
        })
        /**
         * if we are at the max zoom level, open the properties in the swiper
         * the getClusterLeaves function gets all of the properties markers inside of the cluster
         * we then set those properties to be viewed
         * getClusterLeaves documentation here => https://docs.mapbox.com/mapbox-gl-js/api/#geojsonsource#getclusterleaves
         */
        currentZoomLevel === MAX_ZOOM_LEVEL &&
          clusterSource.getClusterLeaves(clusterId, point_count, 0, (err, clusterLeaf) => {
            if (err) return

            this.focusedProperties = this.properties.filter((property) =>
              clusterLeaf.find(
                (clusteredProperty) => clusteredProperty.properties.propertySlug === property.slug
              )
            )
          })
      })
    },
    /**
     * convert properties to a geojson source which is then used
     * for the mapbox clusters and markers
     */
    convertPropertiesToGeoJSON(properties) {
      return properties.map((property) => ({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [property.position.lng, property.position.lat],
        },
        properties: {
          propertySlug: property.slug,
        },
      }))
    },
    /**
     * handle when we need to reset the hovered property,
     * without this once a property is hovered it will stay in the hovered styling
     */
    resetMapMarkerHoverStates() {
      this.properties.forEach((property) => {
        const propertyMarker = document.getElementById(`property-marker-${property.slug}`)
        propertyMarker && propertyMarker.classList.remove('hovered-property')
      })
    },
    /**
     * clicking a marker will pan to the marker and open the property in the swiper
     */
    mapMarketClickHandler({ property, position }) {
      this.mapboxMap.panTo(position)
      this.focusedProperties = [property]

      this.$segment.track('Map Marker Click', {
        category: 'Listings',
        label: 'Mapped Property',
        value: property.id,
      })
    },
    mapMovementHandler() {
      if (this.initialLoadComplete) {
        this.showReCenterButton = true
      }
      this.renderPropertyMarkers()
    },
    // Handles all (re)painting and listing (re)rendering:
    mapboxOnIdleHandler(mapBoxEvent) {
      // Set a flag to mark initial load, enables `Reset` button
      this.initialLoadComplete = true

      /**
       * When the map goes idle, we want to store the bounds of the
       * map so when they return back they are brought back to the same
       * place with the ability to fully reset the map
       */
      const fullMapBounds = this.mapboxMap.getBounds()
      this.renderPropertyMarkers()
      this.resetMapMarkerHoverStates()
      if (this.propertyHovered) {
        this.maybeHighlightCluster(this.propertyHovered)
        this.maybeHighlightPropertyMarker(this.propertyHovered)
      }

      // NOTE: This is an expensive operation, so only proceed if map was panned/zoomed
      if (!mapBoxEvent) return

      // Dyanmically calculate properties by slug which are visible after map movement
      const visibleMapProperties = this.properties
        .filter(({ address: { latitude: lat, longitude: lng } }) =>
          fullMapBounds.contains({ lng, lat })
        )
        .map(({ slug }) => slug)

      // Sends the properties back to re-render the MLP
      this.setVisibleMapProperties(visibleMapProperties)
    },
    /**
     * search for a property marker of the hovered property
     * if it exists add the hovered property class
     */
    maybeHighlightPropertyMarker(property) {
      const hoveredPropertyMarker = document.getElementById(`property-marker-${property.slug}`)
      hoveredPropertyMarker && hoveredPropertyMarker.classList.add('hovered-property')
    },
    /**
     * this function conditionally highlights a cluster based
     * off if the cluster containers the focused property
     */
    async maybeHighlightCluster(property) {
      // grab the propertyListings data source
      const clusterSource = this.mapboxMap.getSource('propertyListings')
      // only attempt to highlight clusters if they have been rendered on the map
      if (!clusterSource) return
      // render all the clusters currently rendered on the map
      let clusters = this.mapboxMap.queryRenderedFeatures({
        layers: ['clusters'],
      })
      // this is used to determine if the focused property was found in a cluster
      let propertyFoundInCluster = false
      // use a for loop since it respects async await / promises
      for (let index = 0; index < clusters.length; index++) {
        const cluster = clusters[index]
        // fetch all property slugs inside of the cluster
        const allClusterProperties = await this.getAllClusterChildren(clusterSource, cluster)
        // check if the hovered property is inside of the cluster
        const clusterIncludesProperty = allClusterProperties.includes(property.slug)
        // if the cluster DOES include the property, create a marker around that cluster
        if (clusterIncludesProperty) {
          // mark the flag as true
          propertyFoundInCluster = true
          const clusterMarkerClassName = `focused-cluster fade-in _${cluster.id}`
          const clusterAlreadyHighlighted = document.querySelector(
            `.focused-cluster._${cluster.id}`
          )
          // if the cluster is already highlighted, do not reanimate
          if (!clusterAlreadyHighlighted) {
            const propertyMarkerElement = document.createElement('div')
            propertyMarkerElement.className = clusterMarkerClassName
            // add cluster to the map
            this.focusedPropertyCluster && this.focusedPropertyCluster.remove()
            try {
              this.focusedPropertyCluster = new window.mapboxgl.Marker(propertyMarkerElement)
                .setLngLat(cluster.geometry.coordinates)
                .addTo(this.mapboxMap)
            } catch (mapboxglError) {
              console.error(mapboxglError)
            }
          }
        }
      }
      /**
       * after attempting to highlight a cluster
       * if no cluster includes the property remove any existing cluster highlight
       */
      if (!propertyFoundInCluster) {
        this.focusedPropertyCluster && this.focusedPropertyCluster.remove()
      }
    },
    // uses a promise to return an array of all cluster property slugs
    getAllClusterChildren(clusterSource, cluster) {
      return new Promise((resolve, reject) => {
        clusterSource.getClusterLeaves(
          cluster.id,
          cluster.properties.point_count,
          0,
          (err, clusterPropertyMarkers) => {
            if (err) return

            const allProductIdsForCluster = clusterPropertyMarkers.map(
              ({ properties: { propertySlug } }) => propertySlug
            )
            resolve(allProductIdsForCluster)
          }
        )
      })
    },
    onListingCardClick(value) {
      this.$segment.track('Map Listing Card Click', {
        category: 'Listings',
        label: 'Clicked Map Card',
        value,
      })
    },
    /**
     * click handler for the Reset map button click
     * logs sentry event then resets map back to initial settings
     */
    resetMapClickHandler() {
      this.$segment.track('Map Reset button Click', {
        category: 'Listings',
        label: 're-center',
      })

      this.propertiesChangeHandler()

      if (this.locationSearchRef) this.locationSearchRef.clear()
    },
    /**
     * this handler is run every time the incoming properties change ( from filtering etc. )
     * this re-evaulates the properties and clusters we need to show
     */
    propertiesChangeHandler() {
      // reset these two variables since this function is called when filtering is done
      // AND when the Reset map button is clicked
      this.initialLoadComplete = false
      this.showReCenterButton = false
      this.setVisibleMapProperties()
      // delete old property markers
      this.setMapboxClusterData(this.mapboxMap)
      this.renderPropertyMarkers()
      const bounds = new window.mapboxgl.LngLatBounds()

      this.configureMapBounds(this.properties, bounds)

      this.mapboxMap.fitBounds(bounds, {
        padding: MAPBOX_PADDING,
        animate: false,
      })
    },
  },
}
</script>

<style lang="scss">
#listings-map {
  position: relative;
  @include mobile {
    position: fixed;
    top: 60px;
  }
  .focused-properties-backdrop {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    background: #0000007e;
  }

  .mapboxgl-ctrl-bottom-left .mapboxgl-ctrl,
  .mapboxgl-ctrl-bottom-right .mapboxgl-ctrl {
    position: fixed;
    bottom: 0;
  }

  .mapboxgl-ctrl-bottom-right .mapboxgl-ctrl {
    right: 0;
  }

  .property-marker {
    z-index: 1;
    opacity: 0;
    animation: show-element 0.25s;
    animation-fill-mode: forwards;
    transition: color 0.25s, background 0.25s;
    cursor: pointer;
    padding: 0.3125rem;
    font-weight: bold;
    font-size: 0.875rem;
    background: white;
    color: $charcoal-60;
    box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1);
    border-radius: 4px;

    &:after {
      transition: color 0.25s, background 0.25s, border 0.25s;
      content: '';
      position: absolute;
      transform: rotate(45deg);
      width: 0.375rem;
      height: 0.375rem;
      bottom: -0.1875rem;
      left: calc(50% - 0.1875rem);
      background: white;
      z-index: -1;
      box-shadow: 0.1875rem 0.125rem 0.25rem rgba(0, 0, 0, 0.1);
      border-right: 0.0625rem solid white;
      border-bottom: 0.0625rem solid white;
    }

    &:hover,
    &.hovered,
    &.hovered-property {
      z-index: 2;
      background: $charcoal-80;
      color: white;
      &:after {
        background: $charcoal-80;
        border-color: $charcoal-80;
      }
    }
  }

  .listings-map-property-slider {
    position: absolute;
    top: 0;
    left: 0;
    z-index: 3;

    .map-swiper {
      box-shadow: 0px 2px 2px rgba(0, 0, 0, 0.1);

      &.swiper-button {
        transition: all 0.25s;
        height: auto;
        width: auto;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0.75rem;
        border-radius: 50%;
        background-color: white;
        border-radius: 25px;
        background-color: white;
        box-shadow: 0 2px 2px rgba(0, 0, 0, 0.13);
      }

      &.swiper-button::before {
        content: none !important;
      }

      &.swiper-button-prev,
      &.swiper-button-next {
        background-image: none;
        left: 32px;
        &:before {
          top: -0.45em;
          left: -0.325em;
          font-size: 48px;
          text-shadow: 0px 2px 7px rgba(0, 0, 0, 0.13);
        }
      }

      &.swiper-button-next {
        left: auto;
        right: 32px;

        &:before {
          left: auto;
          right: 0.1em;
        }
      }
    }

    .map-card-close {
      display: none;
      justify-content: center;
      align-items: center;
      cursor: pointer;
      user-select: none;
      width: 2rem;
      height: 2rem;
      border-radius: 100%;
      background: white;
      position: absolute;
      z-index: 9999;
      right: 1.5rem;
      top: 1.5rem;
      box-shadow: 0px 3px 9px 0px rgba(0, 0, 0, 0.12);
      padding: 10px;
    }

    .swiper-slide-active .map-card-close {
      display: flex;

      &:hover {
        background: $charcoal-5;
      }

      img {
        height: 100%;
        width: 100%;
      }
    }

    .listing-card {
      background: white;
      @include desktop {
        .listing-card-body {
          padding: 1rem;
        }
      }

      .fav-button {
        display: none;
      }
    }

    .swiper-container {
      height: 100%;
      width: 100%;
    }
  }

  .listings-map-swiper {
    .swiper-slide {
      width: 300px;

      @include mobile {
        width: 75%;
      }
    }
  }

  .swiper-card-container {
    height: 100%;
    display: flex;
    width: 100%;
    align-items: center;
  }

  .focused-cluster {
    cursor: pointer;
    height: 41px;
    width: 41px;
    background: transparent;
    border: 3px white solid;
    border-radius: 100%;
    box-shadow: 0 0 8px #0000007a !important;
  }

  .reset-map {
    cursor: pointer;
    z-index: 2;
    position: absolute;
    top: 10px;
    left: 10px;
    padding: 0.5rem 1rem;
    background: $charcoal-60;
    color: white;
    font-size: 14px;
    border-radius: 4px;
    box-shadow: 0 0 8px #0000007a !important;
    @include mobile {
      bottom: 160px;
      top: unset;
    }

    &:hover {
      background: $charcoal-80;
    }
  }
  @include mobile {
    .mapboxgl-ctrl-top-right {
      width: 100%;
      padding: 10px;
      .mapboxgl-ctrl {
        &.mapboxgl-ctrl-geocoder {
          margin: 0;
        }
        &.mapboxgl-ctrl-group {
          margin-right: 0;
        }
      }
    }
  }
}
</style>
